import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Zap, Target, MessageSquare, Sparkles, Lock, PhoneCall, CheckCircle, ArrowRight, Loader2, GraduationCap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Capacitor } from '@capacitor/core';
import { useCompliance } from '@/context/ComplianceContext';
import api from '@/services/api';
import { toast } from 'sonner';

const FeatureIconMap = {
    'learn': Sparkles,
    'chat': MessageSquare,
    'quest': Target,
    'quests': Target,
    'curiosity': Sparkles,
    'upload': Lock,
    'budlee_content': Sparkles,
    'budlee content': Sparkles,
    'advanced_topics': Sparkles,
    'adv_topics': Sparkles,
};

const FeatureNameMap = {
    'quest': 'Quest Generation',
    'quests': 'Quest Generation',
    'chat': 'AI Doubts',
    'learn': 'Learning pack content',
    'budlee_content': 'Budlee Content',
    'budlee content': 'Budlee Content',
    'curiosity': 'Curiosity content',
    'upload': 'Notes upload',
    'advanced_topics': 'Advanced Topics',
    'adv_topics': 'Adv Topics',
};

import { PhoneInputWithCountry } from '@/components/ui/PhoneInputWithCountry';
import { validateE164Phone } from '@/constants/countryCodes';

export default function DailyLimitModal({
    isOpen,
    onClose,
    onSeePlans,
    feature = 'feature',
    usedCount = 0,
    maxCount = 0,
    statusCode = 402,
    directParentUnlock = false
}) {
    const navigate = useNavigate();
    const [showWhatsappModal, setShowWhatsappModal] = useState(directParentUnlock);
    const [hasParentWhatsapp, setHasParentWhatsapp] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [unlockedSuccess, setUnlockedSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [isPaidUser, setIsPaidUser] = useState(false);

    React.useEffect(() => {
        if (isOpen) {
            if (directParentUnlock) {
                setShowWhatsappModal(true);
            }
            const checkProfile = async () => {
                try {
                    const [profRes, subRes] = await Promise.allSettled([
                        api.get('/users/profile/'),
                        api.get('/users/subscription/')
                    ]);

                    if (subRes.status === 'fulfilled') {
                        const sub = subRes.value.data;
                        if (sub?.status === 'ACTIVE' && sub?.plan_name && sub?.plan_name !== 'FREE') {
                            setIsPaidUser(true);
                        } else {
                            setIsPaidUser(false);
                        }
                    }

                    if (profRes.status === 'fulfilled') {
                        const res = profRes.value;
                        const existingNum = res.data?.parent_whatsapp_number || res.data?.phone_no || '';
                        if (existingNum && !phoneNumber) {
                            setPhoneNumber(existingNum);
                        }
                        const hasActiveLink = Boolean(res.data?.parent_whatsapp_link_active);
                        setHasParentWhatsapp(hasActiveLink);
                        if (hasActiveLink) {
                            setShowWhatsappModal(false);
                        }
                    }
                } catch (e) {
                    console.warn('Could not fetch profile in DailyLimitModal:', e);
                }
            };
            checkProfile();
        }
    }, [isOpen, directParentUnlock]);

    const broadcastDailyLimitUnlocked = (respData) => {
        setUnlockedSuccess(true);
        setHasParentWhatsapp(true);
        toast.success("🎉 Request sent on WhatsApp!");
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                Object.keys(window.sessionStorage)
                    .filter(key => key.startsWith('budlee-v2-content-cache:'))
                    .forEach(key => window.sessionStorage.removeItem(key));
            }
        } catch (_) {}
        window.dispatchEvent(new CustomEvent('dailyLimitUnlocked', { detail: respData }));
        window.dispatchEvent(new CustomEvent('budleeProgressUpdated'));
        window.dispatchEvent(new CustomEvent('profileUpdated'));
    };

    const handleDirectResend = async () => {
        if (!phoneNumber) {
            setShowWhatsappModal(true);
            return;
        }
        setLoading(true);
        setErrorMsg('');
        try {
            const resp = await api.post('/users/unlock-daily-limit/', {
                phone_no: phoneNumber
            });
            if (resp.data?.status === 'success') {
                broadcastDailyLimitUnlocked(resp.data);
                setShowWhatsappModal(true);
            } else {
                setShowWhatsappModal(true);
                setErrorMsg(resp.data?.message || 'Failed to unlock daily limit. Please try again.');
            }
        } catch (err) {
            setShowWhatsappModal(true);
            setErrorMsg(err.response?.data?.error || 'Failed to submit phone number.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const Icon = FeatureIconMap[feature.toLowerCase()] || Lock;
    const displayName = FeatureNameMap[feature.toLowerCase()] || feature;
    const isLearnFeature = ['budlee_content', 'budlee content', 'learn'].includes(feature.toLowerCase());
    const is429 = statusCode === 429;
    const { show_subscriptions } = useCompliance();
    const isNative = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
    const showManageAccount = !show_subscriptions && isNative;

    const handleUnlockSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        const valResult = validateE164Phone(phoneNumber);
        if (!valResult.valid) {
            setErrorMsg(valResult.error || 'Please enter a valid international phone number.');
            return;
        }

        setLoading(true);
        try {
            const resp = await api.post('/users/unlock-daily-limit/', {
                phone_no: valResult.sanitized
            });

            if (resp.data?.status === 'success') {
                broadcastDailyLimitUnlocked(resp.data);
            } else {
                setErrorMsg(resp.data?.message || 'Failed to unlock daily limit. Please try again.');
            }
        } catch (err) {
            console.error('Unlock daily limit error:', err);
            const errDetail = err.response?.data?.error || err.response?.data?.message || 'Failed to submit phone number.';
            setErrorMsg(errDetail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-md transition-all duration-300 p-3 sm:p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget && !is429 && !loading) {
                    setShowWhatsappModal(false);
                    onClose();
                }
            }}
        >
            <div className={cn(
                "relative z-[10001] w-full max-w-sm sm:max-w-md h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+2rem))] sm:max-h-[85vh] bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] text-foreground shadow-2xl flex flex-col overflow-y-auto overflow-x-hidden transition-all duration-300 rounded-2xl box-border"
            )}>
                {/* Close Button */}
                {!is429 && !loading && (
                    <button
                        onClick={() => {
                            setShowWhatsappModal(false);
                            onClose();
                        }}
                        className="absolute top-3.5 right-3.5 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 rounded-full bg-[hsl(var(--main-bg))] text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-all border border-[hsl(var(--card-border))] backdrop-blur-sm"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                )}

                {/* Sub-Dialog: Parent WhatsApp Phone Entry Popup */}
                {showWhatsappModal ? (
                    <div className="px-4 py-6 sm:px-6 sm:py-8 flex flex-col items-center text-center w-full box-border">
                        {unlockedSuccess ? (
                            <div className="flex flex-col items-center py-2 sm:py-4 animate-in zoom-in-95 duration-300 w-full text-center">
                                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-500/10 text-[hsl(var(--green-bright))] dark:text-emerald-400 flex items-center justify-center mb-3 sm:mb-4 border border-emerald-500/20 shadow-md">
                                    <CheckCircle className="w-8 h-8 sm:w-9 sm:h-9 text-emerald-500 animate-bounce" />
                                </div>

                                <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 tracking-tight">
                                    Request sent on WhatsApp.
                                </h3>

                                <p className="text-xs sm:text-sm text-muted-foreground max-w-xs mb-5 sm:mb-6 font-medium leading-relaxed">
                                    We have unlocked your access for next 24 hours.
                                </p>

                                <div className="w-full space-y-3 sm:space-y-4 pt-1 sm:pt-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowWhatsappModal(false);
                                            setUnlockedSuccess(false);
                                            onClose();
                                        }}
                                        className="w-full py-2.5 sm:py-3 px-4 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 active:scale-95 border-0"
                                    >
                                        Continue Learning
                                    </button>

                                    <div className="flex items-center justify-center gap-1 text-[11px] sm:text-xs text-muted-foreground pt-0.5 opacity-75 hover:opacity-100 transition-opacity">
                                        <span>Wrong number?</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setUnlockedSuccess(false);
                                                setErrorMsg('');
                                            }}
                                            className="font-medium text-emerald-600 dark:text-emerald-400 hover:underline underline-offset-2 cursor-pointer transition-colors"
                                        >
                                            Change it
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleUnlockSubmit} className="w-full max-w-full flex flex-col items-center">
                                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-emerald-500/10 text-[hsl(var(--green-bright))] dark:text-emerald-400 flex items-center justify-center mb-3 sm:mb-4">
                                    <PhoneCall className="w-6 h-6 sm:w-7 sm:h-7" />
                                </div>

                                <h3 className="text-base sm:text-lg md:text-xl font-bold text-foreground mb-1.5 sm:mb-2 text-center tracking-tight">
                                    Ask Parent to Unlock Access
                                </h3>
                                <p className="text-xs sm:text-sm text-muted-foreground mb-4 sm:mb-6 max-w-xs text-center leading-relaxed">
                                    Enter your parent’s WhatsApp number below. They will receive an exclusive discount link to upgrade your plan, and your access will be unlocked for today!
                                </p>

                                {errorMsg && (
                                    <div className="w-full mb-3 sm:mb-4 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-xs sm:text-sm text-left">
                                        {errorMsg}
                                    </div>
                                )}

                                <div className="w-full max-w-full space-y-2 sm:space-y-2.5 mb-5 sm:mb-6 text-left">
                                    <label className="text-xs sm:text-sm font-semibold text-foreground block">
                                        Parent's WhatsApp Number *
                                    </label>
                                    <PhoneInputWithCountry
                                        value={phoneNumber}
                                        onChange={(val) => setPhoneNumber(val)}
                                        selectClassName="bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] text-foreground"
                                        inputClassName="bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] text-xs sm:text-sm text-foreground placeholder:text-muted-foreground/50"
                                    />
                                    <p className="text-[11px] sm:text-xs text-muted-foreground">
                                        Enter your parent's WhatsApp number with country code.
                                    </p>
                                </div>

                                <div className="flex gap-2 sm:gap-3 w-full">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowWhatsappModal(false);
                                            setErrorMsg('');
                                            if (directParentUnlock) onClose();
                                        }}
                                        disabled={loading}
                                        className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--sidebar-hover))] text-muted-foreground hover:text-foreground font-semibold text-xs sm:text-sm transition-all border border-[hsl(var(--card-border))] active:scale-95"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-1.5 sm:gap-2 active:scale-95 disabled:opacity-50 border-0"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin shrink-0" />
                                                <span>Unlocking...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="truncate">Unlock Access</span>
                                                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                ) : (
                    /* Main Daily Limit Screen */
                    <div className="px-4 py-6 sm:px-8 sm:pb-8 sm:pt-10 flex flex-col items-center text-center w-full box-border">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-4 sm:mb-6 shadow-lg shadow-orange-500/20">
                            <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                        </div>

                        {!hasParentWhatsapp && !isPaidUser ? (
                            <>
                                <h2 className="text-lg sm:text-xl font-bold text-foreground mb-2 sm:mb-3">
                                    You’ve reached today’s free limit.
                                </h2>

                                <div className="space-y-1 mb-5 sm:mb-6 text-center">
                                    <p className="text-foreground font-semibold text-xs sm:text-sm">
                                        Ask your parent to unlock learning.
                                    </p>
                                    <p className="text-muted-foreground text-xs">
                                        They can also see what you’ve learned.
                                    </p>
                                </div>

                                <div className="flex flex-col gap-2.5 sm:gap-3 w-full">
                                    {phoneNumber ? (
                                        <>
                                            <button
                                                onClick={handleDirectResend}
                                                disabled={loading}
                                                className="w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 border-0"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                                                        <span>Sending...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <PhoneCall className="w-4 h-4 text-white shrink-0" />
                                                        <span className="truncate">Ask Parent on WhatsApp to Unlock</span>
                                                    </>
                                                )}
                                            </button>
                                            <div className="flex items-center justify-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground pt-0.5 opacity-90 hover:opacity-100 transition-opacity">
                                                <span>Send to <strong className="text-foreground font-semibold">{phoneNumber.length > 6 ? `${phoneNumber.slice(0, Math.min(6, phoneNumber.length - 4))}••••${phoneNumber.slice(-2)}` : phoneNumber}</strong></span>
                                                <span className="text-muted-foreground/40">•</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowWhatsappModal(true)}
                                                    className="font-medium text-emerald-600 dark:text-emerald-400 hover:underline underline-offset-2 cursor-pointer transition-colors"
                                                >
                                                    Change number
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => setShowWhatsappModal(true)}
                                            className="w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 active:scale-95 border-0"
                                        >
                                            <PhoneCall className="w-4 h-4 text-white shrink-0" />
                                            <span>Ask Parent on WhatsApp</span>
                                        </button>
                                    )}

                                    <div className="flex gap-2 sm:gap-3 w-full">
                                        <button
                                            onClick={onSeePlans}
                                            className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:brightness-105 text-white font-bold shadow-md shadow-emerald-600/20 transition-all active:scale-95 text-xs sm:text-sm truncate border border-emerald-400/30 cursor-pointer"
                                        >
                                            {showManageAccount ? 'Manage Account' : 'See Plans'}
                                        </button>

                                        {!is429 && (
                                            <button
                                                onClick={onClose}
                                                className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200 font-semibold transition-all border border-emerald-500/30 hover:border-emerald-500/50 active:scale-95 text-xs sm:text-sm truncate cursor-pointer"
                                            >
                                                Maybe later
                                            </button>
                                        )}
                                    </div>

                                    {/* Tuition Booking Opportunity */}
                                    <div className="w-full pt-2.5 sm:pt-3 mt-1 border-t border-[hsl(var(--card-border))]/60">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onClose();
                                                navigate('/student/tuitions');
                                            }}
                                            className="w-full py-2.5 sm:py-3 px-3.5 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 text-purple-700 dark:text-purple-300 border border-purple-500/40 font-medium text-xs sm:text-sm flex items-center justify-center gap-2 transition-all active:scale-95 cursor-pointer shadow-sm"
                                        >
                                            <GraduationCap className="w-4 h-4 shrink-0 text-purple-600 dark:text-purple-400" />
                                            <span className="leading-snug">Need help on this chapter? <strong className="underline underline-offset-2 font-bold">Book a Session</strong></span>
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="text-lg sm:text-xl font-bold text-foreground mb-2">
                                    Daily limit reached:
                                </h2>

                                <div className="space-y-1 mb-5 sm:mb-6">
                                    {!is429 && (
                                        <p className="text-muted-foreground font-medium text-xs sm:text-sm">
                                            {isLearnFeature ? '1 section learned today' : (usedCount > 0 && maxCount > 0 ? `${displayName} : ${usedCount}/${maxCount} used today` : '1 section learned today')}
                                        </p>
                                    )}
                                    <p className="text-muted-foreground text-xs sm:text-sm">
                                        Resets in 24 hours
                                    </p>
                                </div>

                                <p className="text-foreground font-semibold mb-5 sm:mb-6 text-xs sm:text-sm">
                                    Upgrade for more access.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
                                    {!is429 && (
                                        <button
                                            onClick={onClose}
                                            className="flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200 font-semibold transition-all border border-emerald-500/30 hover:border-emerald-500/50 active:scale-95 text-xs sm:text-sm cursor-pointer"
                                        >
                                            Maybe later
                                        </button>
                                    )}
                                    <button
                                        onClick={onSeePlans}
                                        className={cn(
                                            "flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:brightness-105 text-white font-bold shadow-lg shadow-emerald-600/20 transition-all active:scale-95 text-xs sm:text-sm border border-emerald-400/30 cursor-pointer",
                                            is429 && "w-full"
                                        )}
                                    >
                                        {showManageAccount ? 'Manage Account' : 'See Plans'}
                                    </button>
                                </div>

                                {/* Tuition Booking Opportunity */}
                                <div className="w-full pt-2.5 sm:pt-3 mt-1 border-t border-[hsl(var(--card-border))]/60">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onClose();
                                            navigate('/student/tuitions');
                                        }}
                                        className="w-full py-2.5 sm:py-3 px-3.5 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 text-purple-700 dark:text-purple-300 border border-purple-500/40 font-medium text-xs sm:text-sm flex items-center justify-center gap-2 transition-all active:scale-95 cursor-pointer shadow-sm"
                                    >
                                        <GraduationCap className="w-4 h-4 shrink-0 text-purple-600 dark:text-purple-400" />
                                        <span className="leading-snug">Need help on this chapter? <strong className="underline underline-offset-2 font-bold">Book a Session</strong></span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
